import { useEffect, useRef, useState } from "react";
import { Toast } from "./Toast";

/**
 * "A new version is available" nudge. No API/version endpoint, no build args: each production build
 * emits a content-hashed bundle (/assets/index-<hash>.js), so when the server starts serving a
 * DIFFERENT hash than the one this tab loaded, a new version has been deployed. We poll the served
 * index.html (cache-busted) and compare. Prompting a reload turns a rolling deploy into a graceful
 * refresh instead of a stale-bundle-vs-new-API failure mid-action. Fail-safe: any hiccup → no toast.
 */
function bundleFrom(s: string | null): string | null {
  if (!s) return null;
  const m = s.match(/\/assets\/index-[A-Za-z0-9._-]+\.js/);
  return m ? m[0] : null;
}

function currentBundle(): string | null {
  const el = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
  return bundleFrom(el?.getAttribute("src") ?? null);
}

async function deployedBundle(): Promise<string | null> {
  try {
    const res = await fetch("/", { cache: "no-store" });
    if (!res.ok) return null;
    return bundleFrom(await res.text());
  } catch {
    return null;
  }
}

export function UpdateToast() {
  const mine = useRef<string | null>(currentBundle());
  const [stale, setStale] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!mine.current) return; // dev / no hashed bundle — nothing to compare against
    let alive = true;
    const check = async () => {
      const live = await deployedBundle();
      if (alive && live && mine.current && live !== mine.current) setStale(true);
    };
    const iv = setInterval(() => void check(), 60_000);
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  if (!stale || dismissed) return null;
  return (
    <Toast
      title="A new version is available"
      body="Reload to get the latest."
      actionLabel="Reload"
      onAction={() => window.location.reload()}
      onClose={() => setDismissed(true)}
    />
  );
}
